import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import TextButton from '@/components/atoms/TextButton/TextButton';
import { TrimSpecsModal } from '@/components/molecules/TrimSpecsModal/TrimSpecsModal';
import { ModelLevelDetailTemplateProps } from '@/components/template/ModelLevelDetailTemplate/types';
import { TrimSpecs, TrimStyleWithSpecs } from '@/lib/schema/build/types';
import {
  VehicleType,
  VehicleTypeWithTrimSpecs,
} from '@/lib/schema/inventory/types';
import {
  HighlightsBadge,
  SortedHighlightsBadges,
} from '@/lib/schema/shared/types';
import { Maybe } from '@/types';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { FC, useEffect, useState } from 'react';
import {
  HighlightIcon,
  HighlightIconId,
  isHighlightIcon,
} from '../../atoms/HighlightIcon';
import { VDPUserControlPanelProps } from '../VDPUserControlPanel/VDPUserControlPanel';

export type HighlightsSectionProps = {
  highlights: SortedHighlightsBadges;
  trimSpecs?: Maybe<TrimSpecs>;
  trimSpecsOptions?: TrimStyleWithSpecs[];
  imageSrc?: string;
  product?:
    | VehicleType
    | VehicleTypeWithTrimSpecs
    | ModelLevelDetailTemplateProps;
  userControlPanelProps?: VDPUserControlPanelProps;
  showSecondaryTrimSpecs?: boolean;
  /** Used to style the "View more" element. Defaults to `'default'`. */
  variant?: 'default' | 'seoTemplate';
};

const HIGHLIGHT_NAME_TO_DISPLAY_VALUE: Readonly<Record<string, string>> = {
  'Est. Range': 'Range',
  'Battery capacity': 'Battery size',
  'Fast charger': 'Fast Charging L3',
};

export const HighlightsSection: FC<HighlightsSectionProps> = ({
  highlights,
  trimSpecs: initialTrimSpecs = null,
  trimSpecsOptions,
  userControlPanelProps,
  product,
  showSecondaryTrimSpecs = true,
  variant = 'default',
}) => {
  const [showSpecsMenu, setShowSpecsMenu] = useState(false);
  const [selectedTrim, setSelectedTrim] = useState<TrimSpecs | null>(
    initialTrimSpecs
  );

  const openTrimSpecsModal = () => setShowSpecsMenu(true);

  useEffect(() => {
    setSelectedTrim(initialTrimSpecs);
  }, [initialTrimSpecs]);

  return (
    <section className="flex flex-1 flex-col rounded-small border-[1px] border-neutral-200 bg-neutral-0 p-l ml:p-xl">
      <div className="grid grid-cols-2 pb-2xl m:grid-cols-3">
        {highlights?.primary.map((highlight: HighlightsBadge, i) => {
          let iconId: HighlightIconId | null = null;

          if (isHighlightIcon(highlight.name)) {
            iconId = highlight.name;
          } else if (highlight.name === 'Est. Range') {
            iconId = 'Range';
          } else if (highlight.name === 'Battery capacity') {
            iconId = 'BatterySize';
          } else if (highlight.name === 'Fast charger') {
            iconId = 'FastCharger';
          } else if (highlight.name === 'Condition') {
            iconId = 'Car';
          } else if (highlight.name === 'State of Registration') {
            iconId = 'Map';
          } else if (highlight.name === 'Drivetrain') {
            iconId = 'DriveTrain';
          }

          return (
            <div
              className="flex h-fit-content flex-col items-center justify-between gap-m border-black p-l ml:flex-row ml:justify-start ml:gap-l ml:px-3xl ml:py-l"
              key={`highlight-primary-${i}`}
            >
              {iconId && (
                <div className="flex size-2xl items-center justify-center pb-s ml:pb-0">
                  <HighlightIcon highlight={iconId} />
                </div>
              )}
              <div className="flex flex-col items-center justify-center ml:h-full ml:items-start ml:justify-start">
                <p className="whitespace-nowrap text-body2Light text-neutral-800">
                  {highlight.name in HIGHLIGHT_NAME_TO_DISPLAY_VALUE
                    ? HIGHLIGHT_NAME_TO_DISPLAY_VALUE[highlight.name]
                    : highlight.name}
                </p>
                <h4 className="whitespace-nowrap text-h4Regular text-neutral-900">
                  {highlight.description}
                </h4>
              </div>
            </div>
          );
        })}
      </div>
      <div>
        {showSecondaryTrimSpecs
          ? highlights.secondary.map(
              (highlight) =>
                highlight.description && (
                  <div
                    key={highlight.name}
                    className="flex justify-between border-b border-neutral-200 py-l"
                  >
                    <div className="text-body1Light text-neutral-800">
                      {highlight.name}
                    </div>
                    <div className="flex items-center gap-s">
                      <div className="text-right text-body1Medium text-neutral-900">
                        {highlight.description}
                      </div>
                      {highlight.extraInfo?.color && (
                        <div
                          className="size-[22px] rounded-full border-[1px] border-neutral-800"
                          style={{ background: highlight.extraInfo.color }}
                        ></div>
                      )}
                    </div>
                  </div>
                )
            )
          : null}
        {product &&
          (!!initialTrimSpecs && variant === 'default' ? (
            <div className="py-s m:text-right">
              <TextButton
                aria-label="view full spec list"
                onClick={openTrimSpecsModal}
                icon={{
                  icon: <ChevronRightIcon className="size-[20px]" />,
                  position: 'right',
                }}
              >
                View full list
              </TextButton>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-between gap-l rounded-xsmall bg-blue-light p-l md:flex-row">
              <div>
                <span className="text-body1Regular">
                  View the full list of details, options, and packages
                  available.
                </span>
              </div>
              <div className="max-w-[188px]">
                <Button
                  aria-label="View all features"
                  variant={ButtonVariants.Secondary}
                  onClick={openTrimSpecsModal}
                >
                  View all features
                </Button>
              </div>
            </div>
          ))}
      </div>
      {product && (
        <TrimSpecsModal
          selectedTrimSpecs={selectedTrim}
          setSelectedTrimSpecs={setSelectedTrim}
          trimSpecsOptions={
            variant !== 'seoTemplate' ? trimSpecsOptions : undefined
          }
          open={showSpecsMenu}
          onClose={() => setShowSpecsMenu(false)}
          className="modal-scroll-bar"
          userControlPanelProps={userControlPanelProps}
          product={product}
        />
      )}
    </section>
  );
};
